import { Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  CheckCircleIcon,
  RemoveVerificationMethodModal,
} from "react-common-ui-library";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  DESCRIPTION_EN,
  EMAIL_LANGUAGE_VALUE,
  LOCALE_EN,
  P_REGULAR,
} from "../../../Constants/CommonConstants";
import { VERIFICATION_METHOD_REMOVED_SUCCESSFULLY } from "../../../Constants/LoginConstants";
import {
  PHONE_HELPER_TEXT,
  phoneEmailConstants,
} from "../../../Constants/LoginVerificationMethodsConstants";
import {
  SLICE_LOGIN,
  THUNK_POST_REMOVE_VERIFICATION_METHOD,
} from "../../../Constants/SliceAndThunkConstants";
import {
  clearLoginApiResponse,
  postRemoveVerificationMethod,
} from "../../../Features/LoginSlice";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import {
  StyledCheckIconPreferredContactDesktopBox,
  StyledCheckIconPreferredContactMobileBox,
  StyledVerificationMethodCheckAndTextBox,
  StyledVerificationMethodOptionalText,
  StyledVerificationMethodsWrapperBox,
} from "../../../Styles/Login/LoginVerificationMethods/LoginVerificationMethodsStyles";
import ConditionalPrimaryStarTooltip from "./ConditionalPrimaryStarTooltip";
import EditOrRemoveVerification from "./EditOrRemoveVerification";
import { getMyProfile } from "../../../Features/MyProfileSlice";
import { manageMyProfileSelector } from "../../../Selectors/MyProfileSelector";
import { manageReferenceDataSelector } from "../../../Selectors/ReferenceDataSelector";
import { manageLocaleSelector } from "../../../Selectors/LocaleSelector";

/**
 * @description LoginVerification Methods Phone Email Section screen allows the user to add, edit or remove verification methods.
 * @param {boolean} isPhone boolean flag to specify phone or email.
 * @param {string} phoneEmailValue string value to specify phone or email value.
 * @param {object} emailOrPhoneIcon Icon to display on screen for phone or email.
 * @param {string} preferredContact string value of preferred contact method.
 * @returns {ReactNode} Renders the verification method details and action buttons.
 */

export const LoginVerificationMethodsPhoneEmailSection = ({
  isPhone,
  phoneEmailValue,
  emailOrPhoneIcon,
  preferredContact,
}) => {
  const intl = useIntl();

  const dispatch = useDispatch();

  const [openRemoveModal, setOpenRemoveModal] = useState(false);

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_LOGIN,
      THUNK_POST_REMOVE_VERIFICATION_METHOD
    )
  );

  const { isSuccess, description } = apiResponses;

  const {
    verificationMethod,
    phoneEmailLabel,
    circleIconId,
    phoneEmailId,
    emailOrPhoneNotRegistered,
  } = phoneEmailConstants(isPhone);

  const handleModalClose = () => {
    setOpenRemoveModal(false);
  };

  const { email, emailLanguage } = useSelector((state) =>
    manageMyProfileSelector(state)
  );

  const { englishEmailLanguage, spanishEmailLanguage } = useSelector((state) =>
    manageReferenceDataSelector(state)
  );

  const { language } = useSelector((state) => manageLocaleSelector(state));

  const displayEmailLanguage = () => {
    if (language === LOCALE_EN) {
      return emailLanguage === DESCRIPTION_EN
        ? englishEmailLanguage.displayEn
        : spanishEmailLanguage.displayEn;
    }
    return emailLanguage === DESCRIPTION_EN
      ? englishEmailLanguage.displayEs
      : spanishEmailLanguage.displayEs;
  };

  const handleRemoveVerificationMethodButtonClick = () => {
    const payload = {
      verificationMethod: verificationMethod,
      [verificationMethod]: phoneEmailValue,
    };
    dispatch(postRemoveVerificationMethod(payload));
  };

  useEffect(() => {
    if (isSuccess && description === VERIFICATION_METHOD_REMOVED_SUCCESSFULLY) {
      handleModalClose();
      dispatch(getMyProfile());
      return;
    }
    dispatch(clearLoginApiResponse(THUNK_POST_REMOVE_VERIFICATION_METHOD));
  }, [description, dispatch, isSuccess]);

  return (
    <StyledVerificationMethodsWrapperBox>
      <StyledVerificationMethodCheckAndTextBox>
        <StyledCheckIconPreferredContactDesktopBox
          preferredContact={phoneEmailValue}
          isPhone={isPhone}
        >
          {phoneEmailValue ? (
            <CheckCircleIcon id={circleIconId} />
          ) : (
            emailOrPhoneIcon
          )}
        </StyledCheckIconPreferredContactDesktopBox>
        <Stack spacing={{ xs: 1.8, md: 1 }}>
          <ConditionalPrimaryStarTooltip
            phoneEmailLabel={phoneEmailLabel}
            preferredContact={preferredContact}
          />
          <StyledVerificationMethodCheckAndTextBox isEmailOrPhoneText={true}>
            <StyledCheckIconPreferredContactMobileBox
              preferredContact={phoneEmailValue}
              isPhone={isPhone}
            >
              {phoneEmailValue ? (
                <CheckCircleIcon id={circleIconId} />
              ) : (
                emailOrPhoneIcon
              )}
            </StyledCheckIconPreferredContactMobileBox>
            <Typography id={phoneEmailId}>
              {phoneEmailValue ||
                intl.formatMessage({ id: emailOrPhoneNotRegistered })}
            </Typography>
          </StyledVerificationMethodCheckAndTextBox>
          {isPhone && (
            <StyledVerificationMethodOptionalText
              variant={P_REGULAR}
              id={PHONE_HELPER_TEXT}
            >
              {intl.formatMessage({ id: PHONE_HELPER_TEXT })}
            </StyledVerificationMethodOptionalText>
          )}
          {!isPhone && email && (
            <Typography id={EMAIL_LANGUAGE_VALUE}>
              {displayEmailLanguage()}
            </Typography>
          )}
        </Stack>
      </StyledVerificationMethodCheckAndTextBox>

      <EditOrRemoveVerification
        phoneEmailValue={phoneEmailValue}
        verificationMethod={verificationMethod}
        setOpenRemoveModal={setOpenRemoveModal}
      />
      <RemoveVerificationMethodModal
        open={openRemoveModal}
        onCloseClickHandler={handleModalClose}
        verificationMethod={verificationMethod}
        verificationMethodValue={phoneEmailValue}
        removeVerificationMethodClickHandler={
          handleRemoveVerificationMethodButtonClick
        }
      />
    </StyledVerificationMethodsWrapperBox>
  );
};

LoginVerificationMethodsPhoneEmailSection.propTypes = {
  isPhone: PropTypes.bool.isRequired,
  phoneEmailValue: PropTypes.string.isRequired,
  emailOrPhoneIcon: PropTypes.object.isRequired,
  preferredContact: PropTypes.bool.isRequired,
};

export default LoginVerificationMethodsPhoneEmailSection;
